import { useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'

import RosterFilters from '../components/RosterFilters'
import RosterTable from '../components/RosterTable'
import SideRail from '../components/SideRail'
import TopBar from '../components/TopBar'
import { useLiveRoster } from '../hooks/useLiveRoster'
import { useNow } from '../hooks/useNow'
import { feedLabel } from '../lib/formatTime'
import { ALL_COMPANIES, companyOptions, filterUsers, pageSlice } from '../lib/rosterFilters'

const WIDE_QUERY = '(min-width:820px)'

export default function TallyPingMonitor({
  // Set REACT_APP_TALLY_WS_URL to point at a backend. With no URL the page connects to
  // nothing and shows an empty table that says so.
  wsUrl = process.env.REACT_APP_TALLY_WS_URL,
  // Rows to start from, for tests that need a populated table without a socket.
  // Never used by the app itself.
  initialUsers,
  offlineFirst = false,
  mode = 'light',
  onToggleMode,
  // Who is signed in, and how to sign out. Both come from the router's wrapper.
  user,
  onLogout,
}) {
  // The socket is the only source of rows. Nothing is invented locally: until the
  // server sends a snapshot the table is empty and says why, because a screen
  // whose job is to report who is online must never show a name it made up.
  const { users, status, lastMessageAt } = useLiveRoster({ url: wsUrl, initialUsers })

  // A local clock, deliberately separate from the feed. It asks for nothing
  // from the server; it just keeps the relative times and row highlights moving.
  const now = useNow()

  // What the person looking at the page has chosen.
  const [query, setQuery] = useState('')
  const [company, setCompany] = useState(ALL_COMPANIES)
  const [tab, setTab] = useState('All')
  const [page, setPage] = useState(0)
  const [perPage, setPerPage] = useState(10)

  const isWide = useMediaQuery(WIDE_QUERY)
  const isDark = mode === 'dark'

  // Everything below is worked out from the state above. Nothing here is stored,
  // so these can never disagree with each other.
  const companies = useMemo(() => companyOptions(users), [users])

  // A company that was on screen can vanish from a later snapshot — its last
  // client deregistered. Filtering by a name no longer in the roster leaves the
  // table empty, which reads as "broken" rather than "nothing selected", so fall
  // back to showing everything.
  const activeCompany = companies.includes(company) ? company : ALL_COMPANIES

  const visible = useMemo(
    () => filterUsers(users, { company: activeCompany, tab, query, offlineFirst }),
    [users, query, activeCompany, tab, offlineFirst],
  )

  // Note that `now` is not a dependency above: the clock ticking every second
  // never re-runs the filtering, only the cheap per-row work inside each row.
  const { total, safePage, start, rows } = pageSlice(visible, page, perPage)

  // Null until the first snapshot lands. Counting seconds from page load would
  // report a freshness we have not actually been given.
  const sinceSync = lastMessageAt === null ? null : Math.max(0, Math.round((now - lastMessageAt) / 1000))
  const syncLabel = feedLabel(status, sinceSync)

  // Any change to what is being filtered sends you back to the first page,
  // otherwise you can end up looking at a page that no longer has anything on it.
  const resetToFirstPage = () => setPage(0)

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'stretch', bgcolor: 'custom.paper' }}>
      <SideRail status={status} isWide={isWide} width={isWide ? 204 : 56} />

      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <TopBar
          company={activeCompany}
          syncLabel={syncLabel}
          isDark={isDark}
          onToggleMode={onToggleMode}
          user={user}
          onLogout={onLogout}
        />

        <Box sx={{ p: '26px 20px 32px', maxWidth: 1180, width: '100%' }}>
          <Stack
            direction="row"
            sx={{ alignItems: 'baseline', gap: '10px', flexWrap: 'wrap', mb: '18px' }}
          >
            <Typography
              component="h1"
              sx={{ fontSize: '1.4rem', fontWeight: 700, letterSpacing: '-0.025em' }}
            >
              Tally Ping Monitor
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: 'custom.ink2' }}>
              Live connection state of every user’s Tally session.
            </Typography>
          </Stack>

          <RosterFilters
            tab={tab}
            onTabChange={(next) => {
              setTab(next)
              resetToFirstPage()
            }}
            query={query}
            onQueryChange={(next) => {
              setQuery(next)
              resetToFirstPage()
            }}
            company={activeCompany}
            companies={companies}
            onCompanyChange={(next) => {
              setCompany(next)
              resetToFirstPage()
            }}
          />

          <RosterTable
            rows={rows}
            status={status}
            // Lets the table tell "the filter hid everything" apart from
            // "the server has told us about nobody".
            hasData={users.length > 0}
            now={now}
            startIndex={start}
            total={total}
            page={safePage}
            perPage={perPage}
            onPageChange={setPage}
            onPerPageChange={(next) => {
              setPerPage(next)
              resetToFirstPage()
            }}
          />
        </Box>
      </Box>
    </Box>
  )
}
